import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Package,
  ShoppingBag,
  Megaphone,
  ListChecks,
  Settings,
  Users,
  KanbanSquare,
  Plus,
  Pencil,
  Check,
  GripVertical,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

type ActionDef = { key: string; label: string; to: string; icon: typeof Package };

const ACTIONS: ActionDef[] = [
  { key: "products", label: "Products", to: "/app/staff/products", icon: Package },
  { key: "shop", label: "Edit shop", to: "/app/customer/shop", icon: ShoppingBag },
  { key: "announcements", label: "Announcements", to: "/app/staff/announcements", icon: Megaphone },
  { key: "jobs-table", label: "All jobs", to: "/app/staff/jobs-table", icon: ListChecks },
  { key: "jobs-board", label: "Jobs board", to: "/app/staff/jobs-board", icon: KanbanSquare },
  { key: "users", label: "Users", to: "/app/staff/users", icon: Users },
  { key: "settings", label: "Settings", to: "/app/settings", icon: Settings },
];
const DEFAULTS = ["products", "shop", "announcements", "jobs-table"];

export function QuickActionsPanel() {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: prefs } = useQuery({
    queryKey: ["profile-quick-actions", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return DEFAULTS;
      const { data } = await supabase
        .from("profiles")
        .select("quick_actions")
        .eq("id", profile.id)
        .single();
      const arr = (data?.quick_actions as unknown as string[]) || [];
      return arr.length ? arr : DEFAULTS;
    },
    enabled: !!profile?.id,
    initialData: DEFAULTS,
  });

  const saveMut = useMutation({
    mutationFn: async (keys: string[]) => {
      if (!profile?.id) throw new Error("No profile");
      const { error } = await supabase
        .from("profiles")
        .update({ quick_actions: keys })
        .eq("id", profile.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-quick-actions"] });
      toast.success("Quick actions updated");
      setOpen(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const visibleActions = useMemo(() => {
    const set = new Set(prefs);
    return ACTIONS.filter((a) => set.has(a.key)).sort(
      (a, b) => prefs.indexOf(a.key) - prefs.indexOf(b.key),
    );
  }, [prefs]);

  const [editing, setEditing] = useState(false);
  const [order, setOrder] = useState<string[]>(prefs);
  useEffect(() => {
    setOrder(prefs);
  }, [prefs]);
  const dragKey = useRef<string | null>(null);

  const move = (from: number, to: number) => {
    setOrder((cur) => {
      if (from === to || from < 0 || to < 0 || from >= cur.length || to >= cur.length) return cur;
      const next = cur.slice();
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  const editingActions = useMemo(() => {
    const set = new Set(order);
    return ACTIONS.filter((a) => set.has(a.key)).sort(
      (a, b) => order.indexOf(a.key) - order.indexOf(b.key),
    );
  }, [order]);

  const finishEditing = () => {
    setEditing(false);
    if (order.join("|") !== prefs.join("|")) saveMut.mutate(order);
  };
  const cancelEditing = () => {
    setOrder(prefs);
    setEditing(false);
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Plus className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Quick actions</h2>
          {editing && (
            <span className="ml-2 text-xs text-muted-foreground">Drag tiles to reorder</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEditing}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={finishEditing} disabled={saveMut.isPending}>
                <Check className="h-4 w-4 mr-1" /> {saveMut.isPending ? "Saving…" : "Done"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                Add / remove
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" /> Customise
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {editing
          ? editingActions.map((a, idx) => (
              <div
                key={a.key}
                draggable
                onDragStart={(e) => {
                  dragKey.current = a.key;
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = order.indexOf(dragKey.current ?? "");
                  move(from, idx);
                  dragKey.current = null;
                }}
                onDragEnd={() => {
                  dragKey.current = null;
                }}
                className="cursor-grab active:cursor-grabbing rounded-2xl border-2 border-dashed border-primary/40 bg-gradient-soft p-4 select-none"
                aria-grabbed={dragKey.current === a.key}
              >
                <div className="flex items-start justify-between">
                  <a.icon className="h-5 w-5 text-primary mb-2" />
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="text-sm font-medium">{a.label}</div>
              </div>
            ))
          : visibleActions.map((a) => (
              <Link key={a.key} to={a.to} className="group">
                <div className="rounded-2xl border bg-gradient-soft p-4 transition-all hover:shadow-md hover:border-primary/40">
                  <a.icon className="h-5 w-5 text-primary mb-2" />
                  <div className="text-sm font-medium">{a.label}</div>
                </div>
              </Link>
            ))}
        {(editing ? editingActions.length : visibleActions.length) === 0 && (
          <div className="col-span-full text-sm text-muted-foreground py-4 text-center">
            No quick actions yet — click Customise to add some.
          </div>
        )}
      </div>

      <CustomiseDialog
        open={open}
        onOpenChange={setOpen}
        selected={editing ? order : prefs}
        onSave={(keys) => {
          // Preserve current order for keys still selected, append new ones at end.
          const base = (editing ? order : prefs).filter((k) => keys.includes(k));
          const added = keys.filter((k) => !base.includes(k));
          const next = [...base, ...added];
          if (editing) setOrder(next);
          saveMut.mutate(next);
        }}
        saving={saveMut.isPending}
      />
    </div>
  );
}

function CustomiseDialog({
  open,
  onOpenChange,
  selected,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  selected: string[];
  onSave: (keys: string[]) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<string[]>(selected);
  useEffect(() => {
    if (open) setDraft(selected);
  }, [open, selected]);
  const toggle = (key: string) => {
    setDraft((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Customise quick actions</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {ACTIONS.map((a) => {
            const checked = draft.includes(a.key);
            return (
              <div key={a.key} className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  <a.icon className="h-4 w-4 text-muted-foreground" />
                  <Label className="cursor-pointer" onClick={() => toggle(a.key)}>
                    {a.label}
                  </Label>
                </div>
                <Switch checked={checked} onCheckedChange={() => toggle(a.key)} />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onSave(draft)} disabled={saving}>
            <Check className="h-4 w-4 mr-1" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
